import { useEffect, useState } from 'react';
import { Send, Clock } from 'lucide-react';
import { Button } from '@/design/components/Button';
import {
  formatDateTime,
  fromLocalInputValue,
  nextWeekdayAt,
  now,
  toLocalInputValue,
  weekdayName,
} from '@/lib/dates';

/** יום חמישי, לפי האינדקס של getDay(). */
const THURSDAY = 4;

/**
 * מתי לפרסם את הסידור.
 *
 * פרסום הוא הרגע שבו חמישים אנשים מקבלים התראה ומתחילים לתכנן
 * לפיה את השבוע. עד היום הלחיצה הוציאה אותו מיד, ולכן מי שסיים
 * לבנות ביום שלישי נאלץ לזכור לחזור ביום חמישי ולא לשכוח.
 *
 * ברירת המחדל היא חמישי הקרוב ב 20:00, השעה שבה הסידור יוצא
 * ממילא. היא מוצעת ולא נכפית: "עכשיו" נמצא כאן באותה מידה.
 *
 * מה שייצא הוא מה שיהיה בלוח באותו רגע, ולא מה שהיה בשעת
 * התזמון. אפשר להמשיך לערוך עד אז, וזו הנקודה.
 */
export function PublishDialog({
  weekLabel,
  shifts,
  isPending,
  onPublishNow,
  onSchedule,
  onCancel,
}: {
  /** "05.04 עד 11.04", כדי שברור איזה שבוע יוצא */
  weekLabel: string;
  shifts: number;
  isPending: boolean;
  onPublishNow: () => void;
  onSchedule: (at: Date) => void;
  onCancel: () => void;
}) {
  // Escape סוגר, כמו בכל דיאלוג אחר במערכת
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const [mode, setMode] = useState<'later' | 'now'>('later');
  const [value, setValue] = useState(() => toLocalInputValue(nextWeekdayAt(THURSDAY, 20)));

  const at = fromLocalInputValue(value);
  const isPast = at !== null && at.getTime() <= now().getTime();
  const canSubmit = mode === 'now' || (at !== null && !isPast);

  return (
    <div className="fixed inset-0 z-40 grid items-end bg-charcoal/50 p-0 sm:place-items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="פרסום הסידור"
        className="grid max-h-[85dvh] w-full gap-3 overflow-y-auto rounded-t-lg bg-surface p-4 sm:max-w-md sm:rounded-lg"
      >
        <div className="grid gap-1">
          <h2 className="m-0 font-display text-display-m font-semibold">פרסום הסידור</h2>
          <p className="m-0 text-body-sm text-muted">
            {weekLabel} · {shifts === 1 ? 'משמרת אחת' : `${shifts} משמרות`}. בפרסום כל מי שמשובץ
            מקבל התראה, ואי אפשר להחזיר אותה.
          </p>
        </div>

        <fieldset className="m-0 grid gap-2 border-0 p-0">
          <legend className="sr-only">מועד הפרסום</legend>

          <label className="grid cursor-pointer gap-1 rounded-sm border border-line p-3 has-checked:border-ink has-checked:bg-olive-soft">
            <span className="flex items-center gap-2 text-body font-medium text-ink">
              <input
                type="radio"
                name="publish-mode"
                checked={mode === 'later'}
                onChange={() => setMode('later')}
                className="size-4 accent-charcoal"
              />
              <Clock size={18} strokeWidth={1.75} aria-hidden="true" />
              תזמון למועד מאוחר יותר
            </span>
            <span className="text-label text-muted">
              הסידור נשאר בעבודה ואפשר להמשיך לערוך. מה שייצא הוא מה שיהיה בלוח אז.
            </span>

            <input
              type="datetime-local"
              value={value}
              min={toLocalInputValue(now())}
              onChange={(event) => {
                setValue(event.target.value);
                setMode('later');
              }}
              aria-label="מועד הפרסום"
              className="min-h-11 rounded-sm border border-muted bg-raised px-2 text-body text-ink"
            />

            {at && !isPast && (
              <span className="text-label text-ink">
                יתפרסם ביום {weekdayName(at)}, {formatDateTime(at)}.
              </span>
            )}
            {isPast && (
              <span role="alert" className="text-label text-bad">
                המועד הזה כבר עבר. לפרסום מיידי יש למטה אפשרות משלה.
              </span>
            )}
            {at === null && value !== '' && (
              <span role="alert" className="text-label text-bad">
                המועד אינו תקין.
              </span>
            )}
          </label>

          <label className="grid cursor-pointer gap-1 rounded-sm border border-line p-3 has-checked:border-ink has-checked:bg-olive-soft">
            <span className="flex items-center gap-2 text-body font-medium text-ink">
              <input
                type="radio"
                name="publish-mode"
                checked={mode === 'now'}
                onChange={() => setMode('now')}
                className="size-4 accent-charcoal"
              />
              <Send size={18} strokeWidth={1.75} aria-hidden="true" className="rtl:-scale-x-100" />
              פרסום עכשיו
            </span>
            <span className="text-label text-muted">ההתראות יוצאות מיד עם הלחיצה.</span>
          </label>
        </fieldset>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="primary"
            disabled={isPending || !canSubmit}
            onClick={() => {
              if (mode === 'now') return onPublishNow();
              if (at) onSchedule(at);
            }}
          >
            {mode === 'now' ? 'פרסום עכשיו' : 'תזמון הפרסום'}
          </Button>
          <Button variant="secondary" onClick={onCancel}>
            ביטול
          </Button>
        </div>
      </div>
    </div>
  );
}
